import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Pause, Play, Ruler, X } from 'lucide-react'
import { EYEBROW } from '../../cc/tokens'
import { haversineKm } from '../../lib/corridor'
import { TuplLiveMap, type TuplReplayOverlay } from './TuplLiveMap'
import {
  fetchTuplRouteStops, positionAt, stopArrivalTimes,
  type TuplDutyClip, type TuplRouteStop,
} from './useTuplApi'

const SPEEDS = [1, 4, 8, 16, 60] as const

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })
}

function fmtDuration(ms: number): string {
  const min = Math.round(ms / 60000)
  if (min < 60) return `${min}m`
  return `${Math.floor(min / 60)}h ${min % 60}m`
}

/** Real distance travelled between two timestamps -- summed along the actual GPS trail,
 * not a straight line between the endpoints, since "how far did the bus actually drive"
 * is the useful ops number, not "how far apart are these two points as the crow flies". */
export function trailDistanceKm(points: TuplDutyClip['points'], start: number, end: number): number {
  const inRange = points.filter((p) => p.ts >= start && p.ts <= end)
  let km = 0
  for (let i = 1; i < inRange.length; i++) {
    const a = inRange[i - 1]
    const b = inRange[i]
    if (a && b) km += haversineKm(a.lat, a.lon, b.lat, b.lon)
  }
  return km
}

/**
 * Whole-duty playback as its own modal window (Command Centre rework Phase 3) -- no
 * longer an inline sidebar panel sharing TuplOpsShell's breakdown-clip clock. It embeds
 * its own TuplLiveMap (same `vehicles={[]} fleetVehicles={[]} showFleetLegend={false}`
 * isolated-historical-view pattern TuplTripAlerts.tsx's own replay modal already uses)
 * and owns its own at/playing/speed/range state -- that coupling with breakdown-clip
 * playback only ever existed because the two shared one map overlay slot; now that duty
 * has its own map, decoupling is the correct state, not a redesign.
 *
 * Plots that day's alert locations on the map (new: `duty.alerts`, already attached by
 * playDuty(), mapped into the same `TuplReplayOverlay.alerts` shape and colour-coded
 * marker layer the trip-alert replay modal already uses) and shows the day's geotags as
 * a list (relocated here now that both the old right-card block (Phase 1) and
 * TuplAlertDetail's own copy (Phase 3) are gone -- this is the only place left that can
 * show an operator a day's geotags at all). The header shows driver/schedule freshly
 * re-read from TIMS when playback opened (`duty.driver_name`/`schedule_code`/
 * `schedule_shift`), not whatever the ambient ~60s live-poll cache had.
 */
export function TuplDutyPlayback({ duty, onClose }: {
  duty: TuplDutyClip
  onClose: () => void
}): JSX.Element {
  const start = duty.started_ts
  const end = duty.ended_ts
  const span = Math.max(1, end - start)
  const xOf = (ts: number) => Math.max(0, Math.min(100, ((ts - start) / span) * 100))

  const [at, setAt] = useState(start)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(8)
  // Two clicks on the duty timeline set a play/distance range: first click (or a click
  // before the current start) sets the start, the next sets the end. null end means
  // "only a start is picked yet" -- distinct from no range at all (both null).
  const [range, setRange] = useState<{ start: number; end: number | null } | null>(null)
  // Owned here, not left to the embedded map's own internal default (Command Centre
  // rework Phase 4) -- threaded into TuplLiveMap's controlled stopDirection/
  // onStopDirectionChange props so the beads and the stop-arrivals list below can never
  // show two different directions.
  const [stopDirection, setStopDirection] = useState<'up' | 'down'>('up')
  const [routeStops, setRouteStops] = useState<TuplRouteStop[]>([])
  // Kept apart from "fetched fine, this route genuinely has none on file" -- collapsing a
  // failed request into the same empty array would render the same affirmative "No stops
  // on file for this route" claim for a network/500 error, telling the operator the wrong
  // thing about why nothing shows (an adversarial-review finding).
  const [routeStopsError, setRouteStopsError] = useState(false)

  useEffect(() => {
    if (!duty.route_code) { setRouteStops([]); setRouteStopsError(false); return }
    // Cleared before the fetch, not left showing the previous direction's stops (or a
    // stale error) for one network round-trip.
    setRouteStops([])
    setRouteStopsError(false)
    let live = true
    fetchTuplRouteStops(duty.route_code, stopDirection)
      .then((s) => { if (live) setRouteStops(s) })
      .catch((e) => { if (live) { console.error('route-stops fetch failed', e); setRouteStopsError(true) } })
    return () => { live = false }
  }, [duty.route_code, stopDirection])

  const arrivalTimes = useMemo(
    () => stopArrivalTimes(routeStops, duty.points),
    [routeStops, duty.points],
  )

  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  useEffect(() => {
    if (!playing) return
    // A fully-set range (both ends picked) bounds playback to just that segment instead
    // of the whole duty -- "play a specific part of the trip".
    const rangeEnd = range?.end ?? end
    const id = window.setInterval(() => {
      setAt((prev) => {
        const next = prev + speed * 200
        if (next >= rangeEnd) { setPlaying(false); return rangeEnd }
        return next
      })
    }, 200)
    return () => window.clearInterval(id)
  }, [playing, range, speed, end])

  const stops = useMemo(
    () => duty.stops.filter((s) => s.ts >= start && s.ts <= end),
    [duty.stops, start, end],
  )
  const alerts = useMemo(
    () => duty.alerts.filter((a) => a.opened_ts != null && a.opened_ts >= start && a.opened_ts <= end),
    [duty.alerts, start, end],
  )
  const rangeKm = useMemo(
    () => (range?.end != null ? trailDistanceKm(duty.points, range.start, range.end) : null),
    [duty.points, range],
  )

  const dutyPos = useMemo(() => positionAt(duty.points, at), [duty.points, at])
  const overlay: TuplReplayOverlay = useMemo(() => ({
    line: duty.points
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map((p) => [p.lon, p.lat] as [number, number]),
    playhead: dutyPos && Number.isFinite(dutyPos.lat) && Number.isFinite(dutyPos.lon)
      ? [dutyPos.lon as number, dutyPos.lat as number] as [number, number]
      : null,
    geotags: duty.stops
      .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon))
      .map((s) => ({ lon: s.lon as number, lat: s.lat as number, name: s.name || s.stage_id || 'stop' })),
    alerts: duty.alerts
      .filter((a) => Number.isFinite(a.lat) && Number.isFinite(a.lon))
      .map((a) => ({
        lon: a.lon as number,
        lat: a.lat as number,
        // `severity` ("CRITICAL"/"WARNING") is not a key of REPLAY_ALERT_COLOR -- only
        // `category` is one of the five declared alert kinds, so a category-less alert
        // must fall through to the generic default color rather than render grey under a
        // severity label.
        kind: a.category || 'alert',
        message: a.note,
      })),
  }), [duty, dutyPos])

  function onPickRange(ts: number): void {
    setRange((prev) => (
      !prev || prev.end != null || ts <= prev.start
        ? { start: ts, end: null }
        : { start: prev.start, end: ts }
    ))
  }

  function onToggle(): void {
    setPlaying((p) => {
      // Starting playback with a full range picked jumps to its start -- "play a
      // specific part of the trip" should not depend on the playhead already sitting
      // inside that window.
      if (!p && range?.end != null && (at < range.start || at > range.end)) {
        setAt(range.start)
      }
      return !p
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-900/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${duty.vehicle_id} duty playback`}
        className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex shrink-0 flex-wrap items-start gap-3 border-b border-slate-200/80 px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <p className={EYEBROW}>Duty playback</p>
            <h2 className="mt-0.5 truncate text-[14px] font-semibold text-[#0F172A]">
              {duty.vehicle_id} · {clock(start)} → {clock(end)}
            </h2>
            <p className="mt-0.5 text-[11px] text-[#64748B]">
              {duty.driver_name || 'No TIMS duty on this plate today'}
              {duty.schedule_shift ? ` · ${duty.schedule_shift}` : ''}
              {duty.schedule_code ? ` · ${duty.schedule_code}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_260px]">
          <div className="relative min-h-0 border-r border-slate-200/80">
            <TuplLiveMap
              vehicles={[]}
              fleetVehicles={[]}
              routes={[]}
              stages={[]}
              down={new Set()}
              selected={null}
              onSelect={() => {}}
              replay={overlay}
              showFleetLegend={false}
              selectedRouteCode={duty.route_code}
              stopDirection={stopDirection}
              onStopDirectionChange={setStopDirection}
            />
          </div>

          <div className="min-h-0 overflow-auto px-3 py-3">
            <p className={EYEBROW}>Geotags today · {duty.stops.length}</p>
            {duty.stops.length === 0 ? (
              <p className="mt-1 text-[11px] text-[#94A3B8]">No stop or boundary crossings recorded.</p>
            ) : (
              <ul className="mt-1 border-l border-[rgba(15,23,42,0.08)]">
                {duty.stops.slice().reverse().map((g, i) => (
                  <li key={`${g.kind}-${g.ts}-${i}`} className="relative py-1 pl-3">
                    <span className={clsx(
                      'absolute -left-[3.5px] top-[9px] h-1.5 w-1.5 rounded-full',
                      g.kind === 'boundary' ? 'bg-[#10B981]' : 'bg-[#2563EB]',
                    )} />
                    <div className="flex items-baseline gap-2 text-[11px]">
                      <span className="shrink-0 font-mono tabular-nums text-[#94A3B8]">
                        {g.ts ? new Date(g.ts).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
                        }) : '—'}
                      </span>
                      <span className="truncate font-medium text-[#334155]">
                        {g.name || (g.kind === 'boundary' ? 'depot boundary' : 'stop')}
                      </span>
                      {g.route_code && (
                        <span className="shrink-0 font-mono text-[10px] text-[#8794A7]">{g.route_code}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* A different concept from the geotags above (route stops, not GPS-confirmed
               boundary/stop crossings), kept as its own section rather than merged into it
               -- Command Centre rework Phase 4. Rendered whenever a route resolved at all
               (an empty `routeStops` fetch still shows the zero state below), so "no stops
               on file for this route" reads as information, not as the feature silently
               not being there. */}
            {duty.route_code && (
              <div className="mt-4">
                <p className={EYEBROW}>
                  Stop arrivals{routeStopsError ? '' : ` · ${routeStops.length}`}
                </p>
                {routeStopsError ? (
                  <p className="mt-1 text-[11px] text-[#9F1239]">Could not load stops for this route.</p>
                ) : routeStops.length === 0 ? (
                  <p className="mt-1 text-[11px] text-[#94A3B8]">No stops on file for this route.</p>
                ) : (
                  <ul className="mt-1 border-l border-[rgba(15,23,42,0.08)]">
                    {routeStops.map((s) => {
                      const ts = arrivalTimes.get(`${s.direction}:${s.seq}`)
                      return (
                        <li key={`${s.direction}-${s.seq}`} className="relative py-1 pl-3">
                          <span className="absolute -left-[3.5px] top-[9px] h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />
                          <div className="flex items-baseline gap-2 text-[11px]">
                            <span className="w-[34px] shrink-0 font-mono tabular-nums text-[#94A3B8]">
                              {ts != null ? new Date(ts).toLocaleTimeString('en-IN', {
                                hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
                              }) : ''}
                            </span>
                            <span className="truncate font-medium text-[#334155]">{s.name}</span>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <section className="shrink-0 border-t border-slate-200/80 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onToggle}
              className="flex h-8 items-center gap-1.5 rounded-full bg-[#0F172A] px-3 text-[12px] font-semibold text-white active:scale-[0.97]"
            >
              {playing ? <Pause size={12} /> : <Play size={12} />}
              {playing
                ? 'Pause'
                : range?.end != null ? 'Play segment' : 'Play'}
            </button>
            <span className="flex gap-0.5">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpeed(s)}
                  className={clsx(
                    'h-8 rounded-full px-2 font-mono text-[11px]',
                    speed === s ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#EEF2F7]',
                  )}
                >
                  {s}×
                </button>
              ))}
            </span>
          </div>

          <div className="mb-2 flex items-center gap-2 rounded-[10px] bg-[#F8FAFC] px-2.5 py-1.5 text-[11px] text-[#64748B]">
            <Ruler size={12} className="shrink-0 text-[#94A3B8]" />
            {range == null ? (
              <span>Click a stop to start a range, click another to measure and play just that stretch.</span>
            ) : range.end == null ? (
              <span>Range start set at {clock(range.start)} — click another stop to finish it.</span>
            ) : (
              <>
                <span className="font-medium text-[#0F172A]">
                  {clock(range.start)} → {clock(range.end)} · {fmtDuration(range.end - range.start)}
                  {rangeKm != null && ` · ${rangeKm.toFixed(1)} km travelled`}
                </span>
                <button
                  type="button"
                  onClick={() => setRange(null)}
                  className="ml-auto shrink-0 rounded-full px-2 py-0.5 font-semibold text-[#2563EB] hover:bg-[rgba(37,99,235,0.08)]"
                >
                  Clear range
                </button>
              </>
            )}
          </div>

          <p className={clsx(EYEBROW, 'mb-1')}>Stops · {stops.length}</p>
          <div className="relative mb-2 h-4 overflow-hidden rounded-full bg-[#EEF2F7]">
            {range?.end != null && (
              <span
                className="absolute top-0 h-full bg-[rgba(37,99,235,0.14)]"
                style={{ left: `${xOf(range.start)}%`, width: `${Math.max(0, xOf(range.end) - xOf(range.start))}%` }}
              />
            )}
            {stops.map((s, i) => (
              <button
                key={`${s.ts}-${i}`}
                type="button"
                title={`${clock(s.ts)} · ${s.name || s.stage_id || 'stop'} · click to set a range point`}
                onClick={() => { setAt(s.ts); onPickRange(s.ts) }}
                className={clsx(
                  'absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white',
                  s.kind === 'boundary' ? 'bg-[#10B981]' : 'bg-[#2563EB]',
                )}
                style={{ left: `${xOf(s.ts)}%` }}
              />
            ))}
            <span className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-[rgba(37,99,235,0.18)]" aria-hidden />
            <Playhead x={xOf(at)} />
          </div>

          <p className={clsx(EYEBROW, 'mb-1')}>Alerts · {alerts.length}</p>
          <div className="relative mb-2 h-4 overflow-hidden rounded-full bg-[#EEF2F7]">
            {range?.end != null && (
              <span
                className="absolute top-0 h-full bg-[rgba(37,99,235,0.14)]"
                style={{ left: `${xOf(range.start)}%`, width: `${Math.max(0, xOf(range.end) - xOf(range.start))}%` }}
              />
            )}
            {alerts.map((a, i) => (
              <button
                key={`${a.id}-${i}`}
                type="button"
                title={`${a.opened_ts != null ? clock(a.opened_ts) : ''} · ${a.category || a.severity || 'alert'}`}
                onClick={() => { if (a.opened_ts != null) setAt(a.opened_ts) }}
                className={clsx(
                  'absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white',
                  a.severity === 'CRITICAL' ? 'bg-[#F43F5E]' : 'bg-[#F59E0B]',
                )}
                style={{ left: `${a.opened_ts != null ? xOf(a.opened_ts) : 0}%` }}
              />
            ))}
            <span className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-[rgba(245,158,11,0.18)]" aria-hidden />
            <Playhead x={xOf(at)} />
          </div>

          <input
            type="range"
            min={start}
            max={end}
            value={Math.max(start, Math.min(end, at))}
            onChange={(e) => setAt(Number(e.target.value))}
            className="w-full accent-[#0F172A]"
            aria-label="Duty playhead"
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[12px] text-[#475569]">
            <span className="font-mono tabular-nums text-[#0F172A]">{clock(at)}</span>
            <span>{stops.length} stops · {alerts.length} alerts · {duty.points.length} GPS</span>
          </div>
        </section>
      </div>
    </div>
  )
}

function Playhead({ x }: { x: number }): JSX.Element {
  return (
    <span
      className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-[#0F172A]"
      style={{ left: `${x}%` }}
    />
  )
}
